import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase'; // Ensure this path is correct for your Supabase client

export interface PopulationRecord {
  Place: string;
  place_fips: string;
  year: string | number;
  total_population: number;
  population_prev_year: number;
  population_growth: number;
  // Add any other fields you expect from the 'acs_bps_metrics_population' view
}

export function usePopulationData(place: string | null, placeFips: string | null) {
  const [data, setData] = useState<PopulationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!place || !placeFips) {
      setLoading(false); // Not loading if no params
      setData([]); // Clear data if no params
      setError(null); // Clear any previous errors
      return;
    }

    setLoading(true);
    setError(null);

    supabase
      .from('acs_bps_metrics_population') // Reverted to the view
      .select('Place, place_fips, year, total_population, population_growth, population_prev_year')
      .eq('Place', place)
      .eq('place_fips', placeFips)
      .order('year', { ascending: true }) // Order by year ascending for the chart
      .then(({ data: supabaseData, error: supabaseError }) => {
        if (supabaseError) {
          console.error('Error fetching population data:', supabaseError);
          setError(supabaseError.message);
          setData([]);
        } else {
          const processedData = supabaseData || [];
          
          if (processedData.length > 1) {
            for (let i = 1; i < processedData.length; i++) {
              const currentYearData = processedData[i];
              // Check if growth is 0 or null and there's a previous year's population value available
              if (currentYearData.population_growth == null || currentYearData.population_growth === 0) {
                const prevYearData = processedData[i - 1];
                if (prevYearData && prevYearData.total_population > 0) {
                  const recalculatedGrowth = ((currentYearData.total_population - prevYearData.total_population) / prevYearData.total_population) * 100;
                  // Update the record with the recalculated growth
                  processedData[i].population_growth = recalculatedGrowth;
                  // Optionally update the previous year population for consistency
                  processedData[i].population_prev_year = prevYearData.total_population;
                }
              }
            }
          }

          setData(processedData);
          
          // Log the latest record for the selected city
          const latest = processedData.slice(-1)[0];
          if (latest) {
            console.log(`Selected City: ${latest.Place}`);
            console.log(`  FIPS: ${latest.place_fips}`);
            console.log(`  Population: ${latest.total_population?.toLocaleString()}`);
            if (typeof latest.population_prev_year !== 'undefined') {
              console.log(`  Previous Population: ${latest.population_prev_year?.toLocaleString()}`);
            }
            if (typeof latest.population_growth !== 'undefined') {
              console.log(`  Growth Rate: ${latest.population_growth?.toFixed(2)}%`);
            }
            console.log(`  Year: ${latest.year}`);
          }
        }
        setLoading(false);
      });
  }, [place, placeFips]);

  return { data, loading, error };
} 